import { DatasetName } from "@/common/constants";
import { ColumnName } from "@/common/fields";
import { MeasureName, MEASURES, MeasureV2 } from "@/common/measures";
import { DIMENSIONS, DimensionV2 } from "@/common/dimensions";
import { FilterDefinition, FILTERS } from "@/common/filters";
import { DatabaseFieldConfig } from "@/common/user";
import { ReportConfigV2 } from "@/common/report-configuration";

// Types for grouped options (matching react-select structure)
export interface FakeMeasure {
  label: string;
  measure: string;
}

interface GroupedMeasure {
  label: string;
  options: Array<MeasureV2 | FakeMeasure>;
}

interface GroupedDimension {
  label: string;
  options: DimensionV2[];
}

interface GroupedFilter {
  label: string;
  options: FilterDefinition[];
}

// Reducer state
export interface ReportDataConfiguratorState {
  selectedDatasets: DatasetName[];
  availableMeasures: GroupedMeasure[];
  availableDimensions: GroupedDimension[];
  availableFilters: GroupedFilter[];
  reportConfig: ReportConfigV2;
  datasetConfigs: Record<DatasetName, DatabaseFieldConfig>;
}

// Action types
type ActionType =
  "toggleDataset" | "restoreSelectedDatasets" | "syncWithReportDesignerState"; // Add more action types as needed

export interface ReportDataConfiguratorAction {
  type: ActionType;
  payload?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

// Constants for grouping
const MEASURE_AND_DIMENSION_GROUP_ORDER = [
  "Benchmark",
  "Time",
  "Recruitment",
  "Demographics",
  "Geography",
  "Organization",
  "Industry",
  "OSHA Incidents",
  "Employee Relations",
  "Culture",
  "Insights Discovery",
  "Employee Goals",
  "Other",
];

// Check that all groups are present in this constant.
const usedGroupNames = new Set(Object.values(MEASURES).map((m) => m.group));
usedGroupNames.forEach((groupName) => {
  if (!MEASURE_AND_DIMENSION_GROUP_ORDER.includes(groupName)) {
    throw new Error(
      `Group name "${groupName}" is used in measures but not included in MEASURE_AND_DIMENSION_GROUP_ORDER constant.`,
    );
  }
});

/**
 *
 * Filtering logic for available measures/dims/etc
1 - start with all whose datasets are in selectedDatasets - however, if only “main” is selected then disallow those with multiple assignments
  (hiding the recruitment columns) - otherwise any match
2 - filter by field config (hide empty fields)
3 - filter by if any measure, dim, or filter present in reportConfig that does not have this measure’s dataset in its list.
     -- another way of stating this is that when main and recruitment are simultaneously selected, we want to wait until we are
     -- forced to remove the recruitment or main fields (to avoid SQL errors) by the actual selection of a config option tied to a different dataset.
4 - filter by existing logic (basically prevent duplicates)
 *
 */

// Helper function to derive available measures from selected datasets
function deriveAvailableMeasures(
  selectedDatasets: DatasetName[],
  workspaceDatasetConfigs: Record<DatasetName, DatabaseFieldConfig>,
  currentConfig: ReportConfigV2,
): GroupedMeasure[] {
  const groupedMeasures: GroupedMeasure[] = [];
  MEASURE_AND_DIMENSION_GROUP_ORDER.forEach((groupName) => {
    const groupOptions: Array<MeasureV2 | FakeMeasure> = [];
    selectedDatasets.forEach((datasetName) => {
      const datasetConfig = workspaceDatasetConfigs[datasetName];
      const emptyFields = datasetConfig?.emptyFields || [];

      const measuresForDataset = Object.values(MEASURES).filter((measure) => {
        const intersects = measure.columnsReferenced.some((field) =>
          emptyFields.includes(field as ColumnName),
        );
        return (
          measure.group === groupName &&
          measure.datasetNames.includes(datasetName) &&
          !intersects
        );
      });
      measuresForDataset.forEach((m) => {
        // Avoid duplicates if the same measure exists in multiple datasets
        const isDuplicate = groupOptions.some(
          (opt) => opt.measure === m.measure,
        );

        // If any measures from other datasets, don't add.
        // We don't need to check dims/filters because
        // the presence of conflicting dims/filters would require
        // a conflicting measure to already be set.
        const hasConflictingMeasure = currentConfig.data.measures.some(
          (measureName) => {
            const measureDatasets = MEASURES[measureName].datasetNames;
            return !measureDatasets.includes(datasetName);
          },
        );

        if (!isDuplicate && !hasConflictingMeasure) {
          groupOptions.push({
            ...m,
            value: m.measure,
          } as MeasureV2 & { value: MeasureName });
        }
      });
    });

    // Because attrition drivers are a separate dataset and it looks bad
    // to have that in the "Report Type" selector but not in measures,
    // we have a fake Measure for Attrition Drivers in the measures list
    // for the HRIS dataset.
    if (selectedDatasets.includes("main") && groupName === "Organization") {
      if (!groupOptions.some((opt) => opt.measure === "ATTRITION_DRIVERS")) {
        groupOptions.push({
          measure: "ATTRITION_DRIVERS" as MeasureName,
          label: "Attrition Top Drivers",
        } as FakeMeasure);
      }
    }

    // Similarly, if we are using the recruitment_applications dataset,
    // we add a fake measure for the Time to Fill Drivers (Top Drivers of Longer Time to Fill)
    if (
      selectedDatasets.includes("recruitment_applications") &&
      groupName === "Recruitment"
    ) {
      if (!groupOptions.some((opt) => opt.measure === "TIME_TO_FILL_DRIVERS")) {
        groupOptions.push({
          measure: "TIME_TO_FILL_DRIVERS" as MeasureName,
          label: "Top Drivers of Longer Time to Fill",
        } as FakeMeasure);
      }
    }

    if (groupOptions.length > 0) {
      groupOptions.sort((a, b) => a.label.localeCompare(b.label));
      groupedMeasures.push({
        label: groupName,
        options: groupOptions,
      });
    }
  });

  return groupedMeasures;
}

// Helper function to derive available dimensions from selected datasets
function deriveAvailableDimensions(
  selectedDatasets: DatasetName[],
  workspaceDatasetConfigs: Record<DatasetName, DatabaseFieldConfig>,
  currentConfig: ReportConfigV2,
): GroupedDimension[] {
  const groupedDimensions: GroupedDimension[] = [];

  MEASURE_AND_DIMENSION_GROUP_ORDER.forEach((groupName) => {
    const groupOptions: DimensionV2[] = [];

    Object.values(DIMENSIONS).forEach((dimension) => {
      if (dimension.group !== groupName) return;

      // Check if dimension is available in any of the selected datasets
      const isAvailable = selectedDatasets.some((datasetName) => {
        const datasetConfig = workspaceDatasetConfigs[datasetName];
        const emptyFields = datasetConfig?.emptyFields || [];

        return (
          dimension.datasetName.includes(datasetName) &&
          !emptyFields.includes(dimension.field as ColumnName)
        );
      });

      const isDuplicate = groupOptions.some(
        (d) => d.dimension === dimension.dimension,
      );

      const hasConflictingMeasure = currentConfig.data.measures.some(
        (measureName) => {
          const measureDatasets = MEASURES[measureName].datasetNames;
          return !measureDatasets.some((d) =>
            dimension.datasetName.includes(d),
          );
        },
      );

      const option = {
        ...dimension,
        value: dimension.dimension,
      } as DimensionV2 & { value: string };

      if (
        workspaceDatasetConfigs[currentConfig.data.datasetName]
          ?.fieldNameOverrides[dimension.dimension]
      ) {
        option.label =
          workspaceDatasetConfigs[
            currentConfig.data.datasetName
          ].fieldNameOverrides[dimension.dimension]!;
      }

      if (isAvailable && !isDuplicate && !hasConflictingMeasure) {
        groupOptions.push(option);
      }
    });

    if (groupOptions.length > 0) {
      groupOptions.sort((a, b) => a.label.localeCompare(b.label));
      groupedDimensions.push({
        label: groupName,
        options: groupOptions,
      });
    }
  });

  return groupedDimensions;
}

// Helper function to derive available filters from selected datasets
function deriveAvailableFilters(
  selectedDatasets: DatasetName[],
  workspaceDatasetConfigs: Record<DatasetName, DatabaseFieldConfig>,
  currentConfig: ReportConfigV2,
): GroupedFilter[] {
  const groupedFilters: GroupedFilter[] = [];

  MEASURE_AND_DIMENSION_GROUP_ORDER.forEach((groupName) => {
    const groupOptions: FilterDefinition[] = [];

    Object.values(FILTERS).forEach((filter) => {
      if (filter.group !== groupName) return;

      // Check if filter is available in any of the selected datasets
      const isAvailable = selectedDatasets.some((datasetName) => {
        const datasetConfig = workspaceDatasetConfigs[datasetName];
        const emptyFields = datasetConfig?.emptyFields || [];

        return (
          filter.datasetName.includes(datasetName) &&
          !emptyFields.includes(filter.field as ColumnName)
        );
      });

      const isDuplicate = groupOptions.some((f) => f.filter === filter.filter);

      const hasConflictingMeasure = currentConfig.data.measures.some(
        (measureName) => {
          const measureDatasets = MEASURES[measureName].datasetNames;
          return !measureDatasets.some((d) => filter.datasetName.includes(d));
        },
      );

      const option = {
        ...filter,
        value: filter.filter,
      } as FilterDefinition & { value: string };

      if (
        workspaceDatasetConfigs[currentConfig.data.datasetName]
          ?.fieldNameOverrides[filter.filter]
      ) {
        option.label =
          workspaceDatasetConfigs[
            currentConfig.data.datasetName
          ].fieldNameOverrides[filter.filter]!;
      }

      if (isAvailable && !isDuplicate && !hasConflictingMeasure) {
        groupOptions.push(option);
      }
    });

    if (groupOptions.length > 0) {
      groupOptions.sort((a, b) => a.label.localeCompare(b.label));
      groupedFilters.push({
        label: groupName,
        options: groupOptions,
      });
    }
  });

  return groupedFilters;
}

// Helper to derive all available fields from selected datasets
function deriveAvailableFields(
  selectedDatasets: DatasetName[],
  workspaceDatasetConfigs: Record<DatasetName, DatabaseFieldConfig>,
  currentConfig: ReportConfigV2,
): Pick<
  ReportDataConfiguratorState,
  "availableMeasures" | "availableDimensions" | "availableFilters"
> {
  return {
    availableMeasures: deriveAvailableMeasures(
      selectedDatasets,
      workspaceDatasetConfigs,
      currentConfig,
    ),
    availableDimensions: deriveAvailableDimensions(
      selectedDatasets,
      workspaceDatasetConfigs,
      currentConfig,
    ),
    availableFilters: deriveAvailableFilters(
      selectedDatasets,
      workspaceDatasetConfigs,
      currentConfig,
    ),
  };
}

// Reducer function
export function reportDataConfiguratorReducer(
  state: ReportDataConfiguratorState,
  action: ReportDataConfiguratorAction,
): ReportDataConfiguratorState {
  const { type, payload } = action;

  switch (type) {
    case "toggleDataset": {
      if (!payload.dataset) return state;

      // main and recruitment get special handling as they can be used together
      if (["main", "recruitment_applications"].includes(payload.dataset)) {
        // filter out non-main/recruitment datasets from the current selected value
        const filteredDatasets = state.selectedDatasets.filter((d) =>
          ["main", "recruitment_applications"].includes(d),
        );

        const datasetIndex = filteredDatasets.indexOf(payload.dataset);
        let newSelectedDatasets: DatasetName[];

        if (datasetIndex === -1) {
          // Add dataset
          newSelectedDatasets = [...filteredDatasets, payload.dataset];
        } else {
          // Remove dataset (but don't allow empty selection)
          if (filteredDatasets.length <= 1) {
            return state;
          }
          newSelectedDatasets = filteredDatasets.filter(
            (d) => d !== payload.dataset,
          );
        }

        return {
          ...state,
          selectedDatasets: newSelectedDatasets,
          ...deriveAvailableFields(
            newSelectedDatasets,
            state.datasetConfigs,
            state.reportConfig,
          ),
        };
      }

      // and update our local state to match the selected dataset in the report designer state
      return {
        ...state,
        selectedDatasets: [payload.dataset],
        ...deriveAvailableFields(
          [payload.dataset],
          state.datasetConfigs,
          state.reportConfig,
        ),
      };
    }

    case "restoreSelectedDatasets": {
      if (!payload.selectedDatasets?.length) return state;

      return {
        ...state,
        selectedDatasets: payload.selectedDatasets,
        ...deriveAvailableFields(
          payload.selectedDatasets,
          state.datasetConfigs,
          state.reportConfig,
        ),
      };
    }

    case "syncWithReportDesignerState": {
      if (!payload.reportConfig) return state;
      if (
        !state.selectedDatasets.includes(payload.reportConfig.data.datasetName)
      ) {
        state.selectedDatasets = [payload.reportConfig.data.datasetName];
      }
      const availableFields = deriveAvailableFields(
        state.selectedDatasets,
        state.datasetConfigs,
        payload.reportConfig,
      );

      return {
        ...state,
        reportConfig: payload.reportConfig,
        ...availableFields,
      };
    }

    default:
      return state;
  }
}
